import clsx from 'clsx';
import React, { useEffect } from 'react';
import ToggleIcon from '../../assets/icons/sidebar-toggle-icon.svg?react';
import { useDispatch, useSelector } from 'react-redux';
import { toggleSwitch } from '../../reducers/switch';
import Button from '../../components/Button';
import EditIcon from '../../assets/icons/edit-icon.svg?react';
import ChevronDownIcon from '../../assets/icons/chevron-down-icon.svg?react';
import WarningIcon from '../../assets/icons/warning.svg?react';

import { toggleModal } from '../../reducers/modalToggle';
import ModalComponent from '../../components/ModalComponent';
import TransactionHistoryModal from '../../components/TransactionHistoryModal';
import { useLocalStorage } from '@uidotdev/usehooks';
import {
  GetEmployeeWorkingHoursResponse,
  LoginResponseData,
  RemoveCardResponseDto,
  SubscriptionDetailsResponseDto,
} from '@EHSNavigator/backend';
import moment from 'moment';
import { useFormik } from 'formik';
import {
  getEditOrganizationDetailsSchema,
  getUpdateCardDetailsSchema,
} from './schema';
import { toast } from 'react-toastify';
import EditOrganizationDetailsModal from '../../components/EditOrganizationDetailsModal';
import { useApiClients } from '../../client/api/useApiClient';
import { useMutation, useQuery } from '@tanstack/react-query';
import Loader from '../../components/Loader';
import UpdateCardDetailsModal from '../../components/UpdateCardDetailsModal';
import { queryClient } from '../../main';
import SubscriptionModal from '../../components/SubscriptionModal';
import EmployeeWorkingHoursModal from '../../components/EmployeeWorkingHoursModal';
import CancelSubscriptionModal from '../../components/CancelSubscriptionModal';

interface OwnerSettingsProps {
  otherClasses?: string;
}

const OwnerSettings: React.FC<OwnerSettingsProps> = ({ otherClasses }) => {
  const [userInfo, setUserInfo] = useLocalStorage<LoginResponseData | null>(
    'userInfo',
    null,
  );
  const [newEmail] = useLocalStorage<string | null>('newEmail', null);
  const [message, setMessage] = useLocalStorage<string | null>('message', null);
  const ownerSettingsClasses = clsx(otherClasses, '');
  const dispatch = useDispatch();
  const { UserSettingsApi } = useApiClients();
  const modalName = useSelector(
    (state: { modalToggle: { modalName: string } }) =>
      state.modalToggle.modalName,
  );

  const { data, isLoading, refetch } = useQuery<SubscriptionDetailsResponseDto>({
    queryKey: ['get-subscription-details'],
    queryFn: async () => {
      const response =
        await UserSettingsApi.userSettingsControllerGetSubscriptionDetails({
          userId: userInfo?.user?.id as string,
        });
      return response?.data;
    },
  });

  const {
    data: employeeWorkingHours = [],
    isLoading: isLoadingEmployeeWorkingHours,
  } = useQuery<GetEmployeeWorkingHoursResponse['employeeHours']>({
    queryKey: ['get-employee-working-hours'],
    queryFn: async () => {
      const response =
        await UserSettingsApi.userSettingsControllerGetOrganzationWorkingHours();
      return response?.data.employeeHours;
    },
  });

  const { StripeApi } = useApiClients();
  const { data: plans = [], isLoading: isLoadingPlans } = useQuery({
    queryKey: ['stripeProducts'],
    queryFn: async () => {
      const response = await StripeApi.stripeControllerGetProducts();
      return response?.data?.products || [];
    },
  });

  const currentPlan = data?.subscription?.currentPlan;
  const billingDetails = data?.subscription?.billingDetails;
  // const transactionHistory = data?.subscription?.transactionHistory || [];

  const INITIAL_VALUES = {
    companyName: userInfo?.user?.org?.name,
    industryType: userInfo?.user?.org?.industryType,
    organizationContact: userInfo?.user?.org?.contact || '',
    industrySize: userInfo?.user?.org?.size,
    organizationAddress: userInfo?.user?.org?.address,
  };

  function getChangedValues(obj1: any, obj2: any): Record<string, any> {
    const changed: Record<string, any> = {};
    const allKeys = new Set([...Object.keys(obj1), ...Object.keys(obj2)]);
    allKeys.forEach((key) => {
      if (obj1[key] !== obj2[key]) {
        changed[key] = obj1[key];
      }
    });
    return changed;
  }

  const changeOrganizationDetailsHandler = useFormik({
    enableReinitialize: true,
    initialValues: INITIAL_VALUES,
    validationSchema: getEditOrganizationDetailsSchema(),
    onSubmit: async (values: any) => {
      try {
        const changedFields = getChangedValues(
          {
            organizationName: values?.companyName,
            industryType: values?.industryType,
            organizationContact: values?.organizationContact || null,
            size: values?.industrySize,
            organizationAddress: values?.organizationAddress,
          },
          {
            organizationName: userInfo?.user?.org?.name,
            industryType: userInfo?.user?.org?.industryType,
            organizationContact: userInfo?.user?.org?.contact || null,
            size: userInfo?.user?.org?.size,
            organizationAddress: userInfo?.user?.org?.address,
          },
        );
        const res =
          await UserSettingsApi.userSettingsControllerUpdateOrganizationDetails(
            {
              id: userInfo?.user?.org?.id as string,
              updateOrganizationSettingDto: {
                ...changedFields,
              },
            },
          );
        setUserInfo((prev: any) => ({
          ...prev,
          user: {
            ...prev?.user,
            org: {
              ...prev?.user?.org,
              ...res?.data?.org,
            },
          },
        }));
        toast.success(res?.data?.message);
        changeOrganizationDetailsHandler?.resetForm();
        dispatch(toggleModal(''));
      } catch (error: any) {
        const errorMessage = error?.response?.data?.message;
        if (Array.isArray(errorMessage)) {
          errorMessage.forEach((msg: string) => toast.error(msg));
        } else {
          toast.error(errorMessage || 'An unexpected error occurred.');
        }
      }
    },
  });

  const updateCardDetailsHandler = useFormik({
    enableReinitialize: true,
    initialValues: {
      line1: billingDetails?.addressObj?.line1 || '',
      line2: billingDetails?.addressObj?.line2 || '',
      city: billingDetails?.addressObj?.city || '',
      state: billingDetails?.addressObj?.state || '',
      postal_code: billingDetails?.addressObj?.postal_code || '',
      country: billingDetails?.addressObj?.country || '',
      name: billingDetails?.name || '',
    },
    validationSchema: getUpdateCardDetailsSchema(),
    onSubmit: async (values: any) => {
      try {
        const changedFields = getChangedValues(
          {
            line1: values?.line1,
            line2: values?.line2,
            city: values?.city,
            state: values?.state,
            postal_code: values?.postal_code,
            country: values?.country,
            name: values?.name,
          },
          {
            line1: billingDetails?.addressObj?.line1 || '',
            line2: billingDetails?.addressObj?.line2 || '',
            city: billingDetails?.addressObj?.city || '',
            state: billingDetails?.addressObj?.state || '',
            postal_code: billingDetails?.addressObj?.postal_code || '',
            country: billingDetails?.addressObj?.country || '',
            name: billingDetails?.name || '',
          },
        );

        const res =
          await UserSettingsApi.userSettingsControllerUpdateCardDetails({
            userId: userInfo?.user?.id as string,
            billingAddressDto: {
              ...changedFields,
            },
          });
        queryClient.setQueryData(
          ['get-subscription-details'],
          (oldData: any) => {
            return {
              ...oldData,
              billingDetails: {
                ...oldData?.billingDetails,
                address: res?.data?.data?.address,
                name: res?.data?.data?.name,
                addressObj: {
                  ...oldData?.billingDetails?.addressObj,
                  ...res?.data?.data?.addressObj,
                },
              },
            };
          },
        );
        toast.success(res?.data?.message);
        updateCardDetailsHandler?.resetForm();
        dispatch(toggleModal(''));
      } catch (error: any) {
        const errorMessage = error?.response?.data?.message;
        if (Array.isArray(errorMessage)) {
          errorMessage.forEach((msg: string) => toast.error(msg));
        } else {
          toast.error(errorMessage || 'An unexpected error occurred.');
        }
      }
    },
  });

  const removeCardMutation = useMutation<RemoveCardResponseDto, Error>({
    mutationFn: async () => {
      const response =
        await UserSettingsApi.userSettingsControllerUserSubscriptionRemoveCard({
          userId: userInfo?.user?.id as string,
        });

      return response.data;
    },
    onSuccess: (data) => {
      window.open(data?.url, '_blank');
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message;
      if (Array.isArray(errorMessage)) {
        errorMessage.forEach((msg: string) => toast.error(msg));
      } else {
        toast.error(errorMessage || 'An unexpected error occurred.');
      }
    },
  });

  function getTimeAgo(date: string) {
    const time = moment(date);
    const now = moment();
    const diffInMinutes = now.diff(time, 'minutes');
    const diffInHours = now.diff(time, 'hours');
    const diffInDays = now.diff(time, 'days');
    if (diffInMinutes < 60) {
      return `${diffInMinutes} minute${diffInMinutes !== 1 ? 's' : ''} ago`;
    } else if (diffInHours < 24) {
      return `${diffInHours} hour${diffInHours !== 1 ? 's' : ''} ago`;
    } else {
      return `${diffInDays} day${diffInDays !== 1 ? 's' : ''} ago`;
    }
  }

  useEffect(() => {
    if (message) {
      setTimeout(() => {
        setMessage(null);
      }, 10000);
    }
  }, [message]);

  if (isLoading || isLoadingPlans || isLoadingEmployeeWorkingHours) {
    return <Loader />;
  }
  return (
    <div className={ownerSettingsClasses} data-testid="owner-settings">
      <div className="border-neutral-tones-text/35 border-b pb-6 lg:pb-12">
        <div className="flex w-full items-start gap-2.5">
          <button
            className="bg-translucent-background/80 border-neutral-tones-text/30 rounded-5 cursor-pointer border p-0.5 lg:hidden"
            onClick={() => dispatch(toggleSwitch())}
          >
            <ToggleIcon />
          </button>
          <div className="w-full">
            <h1 className="h3">Settings</h1>
            <p className="font-inter-variable text-neutral-tones-text/80 -tracking-button mt-15 hidden text-xl leading-6 font-medium lg:block">
              High-risk settings for your EHS Navigator company account.
            </p>
          </div>
        </div>
        {newEmail && (
          <p className="text-inter-variable text-brand-byzantine bg-indicator-blue mx-auto mt-6 w-fit rounded px-1 py-1 text-center text-sm font-medium">
            Verification email has been sent to your {newEmail} email address.
          </p>
        )}
        {message && (
          <p className="text-inter-variable text-brand-byzantine bg-indicator-blue mx-auto mt-6 w-fit rounded px-1 py-1 text-center text-sm font-medium">
            {message}
          </p>
        )}
      </div>
      <div className="border-neutral-tones-text/35 flex w-full flex-col items-start border-b py-6 lg:pt-12 lg:pb-8">
        <div className="border-b-neutral-tones-text/35 w-full border-b pb-8">
          <div className="wfull flex items-center justify-between gap-15">
            <h4 className="text-brand-byzantine">Personal Details </h4>
            <Button
              onClick={() => dispatch(toggleModal('edit-personal-details'))}
              size="smaller"
              otherClasses="min-w-121 w-121"
            >
              <EditIcon className="h-5 w-5 [&>path]:fill-current" /> Edit
            </Button>
          </div>
          <div className="mt-6 grid grid-cols-2 justify-between gap-x-9 gap-y-6 sm:grid-cols-3 xl:grid-cols-5">
            <div className="flex flex-col gap-2.5">
              <h6 className="font-inter-variable text-neutral-tones-text/80 text-base leading-5 font-bold lg:text-lg">
                Name
              </h6>
              <p className="font-inter-variable text-neutral-tones-text text-base leading-5 font-normal">
                {userInfo?.user?.fullName}
              </p>
            </div>
            <div className="flex flex-col gap-2.5">
              <h6 className="font-inter-variable text-neutral-tones-text/80 text-base leading-5 font-bold lg:text-lg">
                Email Address
              </h6>
              <p
                title={userInfo?.user?.email}
                className="font-inter-variable text-neutral-tones-text truncate text-base leading-5 font-normal"
              >
                {userInfo?.user?.email}
              </p>
            </div>
            <div className="flex flex-col gap-2.5">
              <h6 className="font-inter-variable text-neutral-tones-text/80 text-base leading-5 font-bold lg:text-lg">
                Contact Number
              </h6>
              <p className="font-inter-variable text-neutral-tones-text text-base leading-5 font-normal">
                {userInfo?.user?.contact || '---'}
              </p>
            </div>
            <div className="flex flex-col gap-2.5">
              <h6 className="font-inter-variable text-neutral-tones-text/80 text-base leading-5 font-bold lg:text-lg">
                Date Of Birth
              </h6>
              <p className="font-inter-variable text-neutral-tones-text text-base leading-5 font-normal">
                {userInfo?.user?.dob && moment(userInfo?.user?.dob).isValid()
                  ? moment(userInfo?.user?.dob).format('MMMM DD, YYYY')
                  : '---'}
              </p>
            </div>
            <div className="flex flex-col gap-2.5">
              <h6 className="font-inter-variable text-neutral-tones-text/80 text-base leading-5 font-bold lg:text-lg">
                Role
              </h6>
              <p className="font-inter-variable text-neutral-tones-text text-base leading-5 font-normal">
                {userInfo?.user?.role?.name}
              </p>
            </div>
          </div>
        </div>
        <div className="w-full pt-8">
          <div className="flex w-full items-center justify-between gap-15">
            <h4 className="text-brand-byzantine">Company Details</h4>

            <Button
              onClick={() => dispatch(toggleModal('edit-organization-details'))}
              size="smaller"
              otherClasses="min-w-121 w-121"
            >
              <EditIcon className="h-5 w-5 [&>path]:fill-current" /> Edit
            </Button>
          </div>
          <div className="mt-6 grid grid-cols-2 justify-between gap-x-9 gap-y-6 sm:grid-cols-3 xl:grid-cols-4">
            <div className="col-span-2 flex flex-col gap-2.5 sm:col-span-1">
              <h6 className="font-inter-variable text-neutral-tones-text/80 text-base leading-5 font-bold lg:text-lg">
                Company Name
              </h6>
              <p className="font-inter-variable text-neutral-tones-text text-base leading-5 font-normal">
                {userInfo?.user?.org?.name}
              </p>
            </div>
            <div className="flex flex-col gap-2.5">
              <h6 className="font-inter-variable text-neutral-tones-text/80 text-base leading-5 font-bold lg:text-lg">
                Registration Date
              </h6>
              <p className="font-inter-variable text-neutral-tones-text text-base leading-5 font-normal">
                {moment(userInfo?.user?.org?.createdAt).format('MMMM DD, YYYY')}
              </p>
            </div>
            <div className="flex flex-col gap-2.5">
              <h6 className="font-inter-variable text-neutral-tones-text/80 text-base leading-5 font-bold lg:text-lg">
                Company Address
              </h6>
              <p className="font-inter-variable text-neutral-tones-text text-base leading-5 font-normal">
                {userInfo?.user?.org?.address}
              </p>
            </div>
            {userInfo?.user?.org?.contact && (
              <div className="flex flex-col gap-2.5">
                <h6 className="font-inter-variable text-neutral-tones-text/80 text-base leading-5 font-bold lg:text-lg">
                  Organization Contact
                </h6>
                <p className="font-inter-variable text-neutral-tones-text text-base leading-5 font-normal">
                  {userInfo?.user?.org?.contact}
                </p>
              </div>
            )}
            <div className="flex flex-col gap-2.5">
              <h6 className="font-inter-variable text-neutral-tones-text/80 text-base leading-5 font-bold lg:text-lg">
                Industry Type
              </h6>
              <p className="font-inter-variable text-neutral-tones-text text-base leading-5 font-normal">
                {userInfo?.user?.org?.industryType}
              </p>
            </div>
            <div className="flex flex-col gap-2.5">
              <h6 className="font-inter-variable text-neutral-tones-text/80 text-base leading-5 font-bold lg:text-lg">
                Company Size
              </h6>
              <p className="font-inter-variable text-neutral-tones-text text-base leading-5 font-normal">
                {userInfo?.user?.org?.size}
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className="border-neutral-tones-text/35 w-full border-b py-6 lg:py-8">
        <div className="mb-6 flex items-center justify-between">
          <h4 className="text-brand-byzantine">Password</h4>
          <Button
            onClick={() => dispatch(toggleModal('change-password'))}
            size="smaller"
            otherClasses="min-w-121 w-121"
          >
            <EditIcon className="h-5 w-5 [&>path]:fill-current" /> Edit
          </Button>
        </div>
        {userInfo?.user?.setPasswordAt ? (
          <p className="font-inter-variable text-neutral-tones-text text-base leading-5 font-normal italic lg:text-lg">
            Your Password was changed{' '}
            {getTimeAgo(userInfo?.user?.setPasswordAt as string)}. Never share
            your password with anyone!
          </p>
        ) : (
          <p className="font-inter-variable text-neutral-tones-text text-base leading-5 font-normal italic lg:text-lg">
            Your Password was never changed. Never share your password with
            anyone!
          </p>
        )}
      </div>
      <div className="border-neutral-tones-text/35 w-full border-b py-6 lg:py-8">
        <div className="flex items-center justify-between">
          <h4 className="text-brand-byzantine relative">
            Employee Annual Working Hours{' '}
            <button className="group !font-inter-variable inline-block !tracking-normal md:relative">
              <WarningIcon className="h-6 w-6 translate-y-1" />
              <p className="bg-neutral-tones-sidebar text-neutral-tones-text absolute bottom-full left-2/4 z-10 hidden w-full -translate-x-2/4 rounded px-2 py-1 text-center text-xs font-normal shadow-lg group-hover:block group-focus:block md:min-w-235">
                This is the total working hours accomplished by all employees in
                your organization, which will be used to calculate the TRIR.
              </p>
            </button>
          </h4>
          <Button
            size="smaller"
            otherClasses="min-w-121 w-121"
            onClick={() => dispatch(toggleModal('employee-working-hours'))}
          >
            <EditIcon className="h-5 w-5 [&>path]:fill-current" /> Edit
          </Button>
        </div>
      </div>
      {
        data?.subscription && (
          <div className="border-neutral-tones-text/35 w-full border-b py-6 lg:py-8">
            <div className="mb-6 flex items-center justify-between">
              <h4 className="text-brand-byzantine">Seats & Billing</h4>
              <Button
                onClick={() => dispatch(toggleModal('manage-subscription'))}
                size="smaller"
              >
                <EditIcon className="hidden h-5 w-5 sm:block [&>path]:fill-current" />
                Manage Subscription
              </Button>
            </div>
            <div className="bg-card-backgrounds/5 rounded-10 grid w-full grid-cols-1 gap-2.5 xl:grid-cols-2">
              <div className="bg-brand-byzantine rounded-10 p-6">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-translucent-background/65 font-inter-variable text-xs leading-4 font-bold sm:text-base sm:leading-5">
                    CURRENT PLAN
                  </p>

                  <p className="bg-brand-posh-pink font-inter-variable rounded-lg px-4 py-1 text-xs leading-4 font-bold text-white capitalize sm:text-base sm:leading-5">
                    {currentPlan?.subscription?.status}
                  </p>
                </div>
                <div className="border-translucent-background/65 mt-15 flex items-start justify-between gap-2.5 border-b pb-9">
                  <div className="max-w-191">
                    <h3 className="text-white capitalize">{currentPlan?.name}</h3>
                    <p className="font-inter-variable mt-1 text-sm font-normal text-white">
                      {currentPlan?.description}
                    </p>
                  </div>
                  <div>
                    <h1 className="text-white">${currentPlan?.price}</h1>
                    <p className="font-inter-variable text-sm font-bold text-white/80">
                      {currentPlan?.priceDescription}
                    </p>
                  </div>
                </div>
                <div className="flex items-start justify-between pt-9">
                  <div className="flex flex-col gap-1">
                    <p className="text-translucent-background/65 font-inter-variable text-xs leading-4 font-normal sm:text-base sm:leading-5 sm:font-bold">
                      ACTIVE SINCE
                    </p>
                    <p className="font-inter-variable text-xs leading-4 font-bold text-white sm:text-base sm:leading-5">
                      {currentPlan?.activeSince}
                    </p>
                  </div>
                  <div className="flex flex-col gap-1">
                    <p className="text-translucent-background/65 font-inter-variable text-xs leading-4 font-normal sm:text-base sm:leading-5 sm:font-bold">
                      LAST BILLED ON
                    </p>
                    <p className="font-inter-variable text-xs leading-4 font-bold text-white sm:text-base sm:leading-5">
                      {currentPlan?.lastBilled}
                    </p>
                  </div>
                  <div className="flex flex-col gap-1">
                    <p className="text-translucent-background/65 font-inter-variable text-xs leading-4 font-normal sm:text-base sm:leading-5 sm:font-bold">
                      NEXT BILLING ON
                    </p>
                    <p className="font-inter-variable text-xs leading-4 font-bold text-white sm:text-base sm:leading-5">
                      {currentPlan?.nextBilling}
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex flex-col justify-between p-6">
                <div className="pb-15 lg:pb-0">
                  <div className="mb-5 flex justify-between">
                    <p className="text-neutral-tones-text/80 font-inter-variable text-base leading-5 font-bold">
                      BILLING DETAILS
                    </p>
                  </div>
                  <div className="mb-5 flex items-start gap-2.5 sm:gap-8">
                    <div className="flex flex-col items-start gap-2.5 sm:flex-row">
                      <img
                        src={billingDetails?.brandImage}
                        alt="card-image"
                        className="max-h-8 max-w-11 rounded"
                      />
                      <div>
                        <h6 className="font-inter-variable text-brand-byzantine mb-1 text-sm font-bold">
                          {billingDetails?.name}
                        </h6>
                        <p className="text-neutral-tones-text mb-1 text-sm font-normal">
                          **** {billingDetails?.last4}
                        </p>
                        <div className="flex items-stretch gap-1">
                          <button
                            onClick={() =>
                              dispatch(toggleModal('update-card-details'))
                            }
                            className="font-inter-variable text-brand-byzantine cursor-pointer text-[10px] font-bold whitespace-nowrap"
                          >
                            Update Details
                          </button>
                          <span className="border-l-neutral-tones-text/35 block border-l"></span>
                          <button
                            onClick={() => removeCardMutation.mutate()}
                            className="font-inter-variable text-brand-posh-pink cursor-pointer text-[10px] font-bold whitespace-nowrap"
                          >
                            Remove Card
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="pt-11 sm:pt-0">
                      <h6 className="font-inter-variable text-neutral-tones-text/80 mb-2.5 cursor-pointer text-[10px] font-bold">
                        Address
                      </h6>
                      <p className="font-inter-variable text-neutral-tones-text max-w-40 text-sm leading-4 font-normal">
                        {billingDetails?.address}
                      </p>
                    </div>
                  </div>
                  <Button
                    otherClasses="!text-brand-byzantine !text-xs !font-bold"
                    variant="link"
                    onClick={() => dispatch(toggleModal('transaction-history'))}
                  >
                    View Transaction History
                    <ChevronDownIcon className="h-5 w-5 -rotate-90 [&>path]:fill-current" />
                  </Button>
                </div>
                <div className="border-t-neutral-tones-text/35 border-t pt-15 lg:border-t-0">
                  <p className="text-neutral-tones-text/80 font-inter-variable text-base leading-5 font-bold">
                    SEATS
                  </p>
                  <div className="mt-2.5 flex flex-wrap justify-between gap-1 sm:mt-5">
                    <p className="text-neutral-tones-text/80 font-inter-variable text-sm leading-5 font-normal italic">
                      Your plan currently has{' '}
                      <span className="text-brand-byzantine">
                        {billingDetails?.seats}
                      </span>{' '}
                      active seats.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )
      }
      {
        data?.manualSubscription && (
          <div className="border-neutral-tones-text/35 w-full border-b py-6 lg:py-8">
            <div className="mb-6 flex items-center justify-between">
              <h4 className="text-brand-byzantine">Seats & Billing</h4>
              {/* <Button
                onClick={() => dispatch(toggleModal('manage-subscription'))}
                size="smaller"
              >
                <EditIcon className="hidden h-5 w-5 sm:block [&>path]:fill-current" />
                Manage Subscription
              </Button> */}
            </div>
            <div className="bg-card-backgrounds/5 rounded-10 grid w-full grid-cols-1 gap-2.5 xl:grid-cols-2">
              <div className="bg-brand-byzantine rounded-10 p-6">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-translucent-background/65 font-inter-variable text-xs leading-4 font-bold sm:text-base sm:leading-5">
                    Manual Subscription Plan
                  </p>

                  <p className="bg-brand-posh-pink font-inter-variable rounded-lg px-4 py-1 text-xs leading-4 font-bold text-white capitalize sm:text-base sm:leading-5">
                    {data.manualSubscription?.status}
                  </p>
                </div>
                <div className="border-translucent-background/65 mt-15 flex items-start justify-between gap-2.5 border-b pb-9">
                  <div className="max-w-191">
                    <h3 className="text-white capitalize">{'Join Now'}</h3>
                    <p className="font-inter-variable mt-1 text-sm font-normal text-white">
                      This plan was added manually and managed outside the system.
                    </p>
                  </div>

                  <div>
                    {/* <h1 className="text-white">
                      Manual Subscription Plan
                    </h1>
                    <p className="font-inter-variable text-sm font-bold text-white/80">
                      This plan allows up to {data.manualSubscription?.licenseCount} licenses for your team.
                      It was created on {moment(data.manualSubscription?.createdAt).format('MMMM Do, YYYY')}{" "}
                      and expires on {moment(data.manualSubscription?.expiresAt).format('MMMM Do, YYYY')}.
                    </p> */}
                  </div>
                </div>
                <div className="flex items-start justify-between pt-9">
                  <div className="flex flex-col gap-1">
                    <p className="text-translucent-background/65 font-inter-variable text-xs leading-4 font-normal sm:text-base sm:leading-5 sm:font-bold">
                      ACTIVE SINCE
                    </p>
                    <p className="font-inter-variable text-xs leading-4 font-bold text-white sm:text-base sm:leading-5">
                      {moment(data.manualSubscription?.activatedAt).format('MMMM Do, YYYY')}
                    </p>
                  </div>
                  {/* <div className="flex flex-col gap-1">
                    <p className="text-translucent-background/65 font-inter-variable text-xs leading-4 font-normal sm:text-base sm:leading-5 sm:font-bold">
                      LAST UPDATED ON
                    </p>
                    <p className="font-inter-variable text-xs leading-4 font-bold text-white sm:text-base sm:leading-5">
                      {moment(data.manualSubscription?.updatedAt).format('MMMM Do, YYYY')}
                    </p>
                  </div> */}
                  <div className="flex flex-col gap-1">
                    <p className="text-translucent-background/65 font-inter-variable text-xs leading-4 font-normal sm:text-base sm:leading-5 sm:font-bold">
                      NEXT BILLING ON
                    </p>
                    <p className="font-inter-variable text-xs leading-4 font-bold text-white sm:text-base sm:leading-5">
                      {moment(data.manualSubscription?.expiresAt).format('MMMM Do, YYYY')}
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex flex-col justify-between p-6">
                {/* <div className="pb-15 lg:pb-0">
                  <div className="mb-5 flex justify-between">
                    <p className="text-neutral-tones-text/80 font-inter-variable text-base leading-5 font-bold">
                      BILLING DETAILS
                    </p>
                  </div>
                  <div className="mb-5 flex items-start gap-2.5 sm:gap-8">
                    <div className="flex flex-col items-start gap-2.5 sm:flex-row">
                      <img
                        src={billingDetails?.brandImage}
                        alt="card-image"
                        className="max-h-8 max-w-11 rounded"
                      />
                      <div>
                        <h6 className="font-inter-variable text-brand-byzantine mb-1 text-sm font-bold">
                          {billingDetails?.name}
                        </h6>
                        <p className="text-neutral-tones-text mb-1 text-sm font-normal">
                          **** {billingDetails?.last4}
                        </p>
                        <div className="flex items-stretch gap-1">
                          <button
                            onClick={() =>
                              dispatch(toggleModal('update-card-details'))
                            }
                            className="font-inter-variable text-brand-byzantine cursor-pointer text-[10px] font-bold whitespace-nowrap"
                          >
                            Update Details
                          </button>
                          <span className="border-l-neutral-tones-text/35 block border-l"></span>
                          <button
                            onClick={() => removeCardMutation.mutate()}
                            className="font-inter-variable text-brand-posh-pink cursor-pointer text-[10px] font-bold whitespace-nowrap"
                          >
                            Remove Card
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="pt-11 sm:pt-0">
                      <h6 className="font-inter-variable text-neutral-tones-text/80 mb-2.5 cursor-pointer text-[10px] font-bold">
                        Address
                      </h6>
                      <p className="font-inter-variable text-neutral-tones-text max-w-40 text-sm leading-4 font-normal">
                        {billingDetails?.address}
                      </p>
                    </div>
                  </div>
                  <Button
                    otherClasses="!text-brand-byzantine !text-xs !font-bold"
                    variant="link"
                    onClick={() => dispatch(toggleModal('transaction-history'))}
                  >
                    View Transaction History
                    <ChevronDownIcon className="h-5 w-5 -rotate-90 [&>path]:fill-current" />
                  </Button>
                </div> */}
                <div className="border-t-neutral-tones-text/35 border-t pt-15 lg:border-t-0">
                  <p className="text-neutral-tones-text/80 font-inter-variable text-base leading-5 font-bold">
                    SEATS
                  </p>
                  <div className="mt-2.5 flex flex-wrap justify-between gap-1 sm:mt-5">
                    <p className="text-neutral-tones-text/80 font-inter-variable text-sm leading-5 font-normal italic">
                      Your plan currently has{' '}
                      <span className="text-brand-byzantine">
                        {data.manualSubscription?.seats}
                      </span>{' '}
                      active seats and {' '}
                      <span className="text-brand-byzantine">
                        {data?.manualSubscription.licenseCount - data.manualSubscription.seats}
                      </span>{' '}
                      remaining seats.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )
      }
      {/* <div className="border-neutral-tones-text/35 w-full border-b py-6 lg:py-8">
        <div className="mb-6 flex items-center justify-between">
          <h4 className="text-brand-posh-pink">Delete an account</h4>
          <Button
            onClick={() => dispatch(toggleModal('delete-account'))}
            size="smaller"
            otherClasses="whitespace-nowrap bg-brand-posh-pink hover:text-brand-posh-pink hover:border-brand-posh-pink"
          >
            <TrashIcon className="hidden h-5 w-5 sm:block [&>path]:stroke-current" />
            Delete Your Account
          </Button>
        </div>
        <p className="font-inter-variable text-neutral-tones-text text-base leading-5 font-normal italic lg:text-lg">
          Once you delete your account, there is no going back. Please be
          certain.
        </p>
      </div> */}
      {(modalName === 'transaction-history' ||
        modalName === 'edit-organization-details' ||
        modalName === 'update-card-details' ||
        modalName === 'employee-working-hours' ||
        modalName === 'cancel-subscription' ||
        modalName === 'manage-subscription') && (
          <ModalComponent
            otherClasses="px-4 lg:px-0 py-6"
            additionalClass={clsx(
              'rounded-2xl h-fit max-h-full w-full',
              modalName === 'edit-organization-details' ||
                modalName === 'update-card-details' ||
                modalName === 'manage-subscription'
                ? 'p-5 lg:px-9 lg:py-6'
                : 'p-5 lg:p-6',
              modalName === 'cancel-subscription' ? 'max-w-573' : 'max-w-3xl',
            )}
            customClose={() => {
              if (modalName === 'cancel-subscription') {
                dispatch(toggleModal('manage-subscription'));
              } else {
                updateCardDetailsHandler?.resetForm();
                changeOrganizationDetailsHandler?.resetForm();
                dispatch(toggleModal(''));
              }
            }}
          >
            {modalName === 'edit-organization-details' && (
              <EditOrganizationDetailsModal
                formHandler={changeOrganizationDetailsHandler}
                handleClose={() => {
                  changeOrganizationDetailsHandler?.resetForm();
                  dispatch(toggleModal(''));
                }}
              />
            )}
            {modalName === 'transaction-history' && (
              <TransactionHistoryModal
                data={data as SubscriptionDetailsResponseDto}
                handleClose={() => {
                  dispatch(toggleModal(''));
                }}
              />
            )}
            {modalName === 'update-card-details' && (
              <UpdateCardDetailsModal
                formHandler={updateCardDetailsHandler}
                handleClose={() => {
                  updateCardDetailsHandler?.resetForm();
                  dispatch(toggleModal(''));
                }}
              />
            )}
            {modalName === 'manage-subscription' && (
              <SubscriptionModal
                plans={plans}
                refetch={refetch}
                data={data as SubscriptionDetailsResponseDto}
              />
            )}
            {modalName === 'employee-working-hours' && (
              <EmployeeWorkingHoursModal
                employeeWorkingHours={employeeWorkingHours}
                handleClose={() => {
                  dispatch(toggleModal(''));
                }}
              />
            )}
            {modalName === 'cancel-subscription' && (
              <CancelSubscriptionModal
                handleClose={() => {
                  dispatch(toggleModal('manage-subscription'));
                }}
              />
            )}
          </ModalComponent>
        )}
    </div>
  );
};

export default OwnerSettings;
